/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;

    this.form = this.container.querySelector('form.searchform');
    this.input = this.container.querySelector('input[type="search"]');
    this.results = this.container.querySelector('.side-panel-content--has-tabs');
    if (!this.form || !this.input || !this.results) return;

    this.searchRequestId = 0;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', (event) => {
      if (!this.getQuery()) event.preventDefault();
    });

    this.input.addEventListener('input', debounce(() => this.onChange(), 300));

    this.results.addEventListener('click', (event) => {
      if (event.target.closest('#search-results-submit')) this.form.submit();
    });

    document.querySelectorAll('.thb-quick-search').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
        if (this.container.classList.contains('active')) {
          setTimeout(() => this.input.focus({ preventScroll: true }), 100);
          dispatchCustomEvent('search:open');
        }
      });
    });

    document.addEventListener('search:open', () => {
      this.scheduleSliderSync();
    });

    this.container.addEventListener('lazyloaded', (event) => {
      if (event.target.closest('.side-panel-content--initial .product-card-slider')) this.syncSliders();
    }, true);
  }

  scheduleSliderSync() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncSliders());
    });
  }

  syncSliders() {
    if (typeof Flickity === 'undefined') return;

    const isMobile = window.innerWidth <= 768;

    this.container.querySelectorAll('.side-panel-content--initial .product-card-slider.flickity-enabled').forEach((slider) => {
      if (getComputedStyle(slider).display === 'none') return;

      const flkty = Flickity.data(slider);
      if (!flkty) return;

      flkty.resize();
      if (isMobile) flkty.select(0);
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!this.results) return;

    if (!searchTerm) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.results.classList.remove('loading');
      this.results.classList.remove('active');
      return;
    }
    this.results.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  parseSectionHtml(text, sectionId) {
    const section = new DOMParser().parseFromString(text, 'text/html').querySelector(`#shopify-section-${sectionId}`);
    return section ? section.innerHTML : '';
  }

  getSearchResults(searchTerm) {
    if (!this.results) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.searchRequestId += 1;
    const requestId = this.searchRequestId;

    this.results.classList.add('loading');
    const query = encodeURIComponent(searchTerm);
    const typeParam = encodeURIComponent('resources[type]');
    const limitParam = encodeURIComponent('resources[limit]');
    const productsUrl = `${theme.routes.predictive_search_url}?q=${query}&${typeParam}=product&${limitParam}=6&section_id=predictive-search`;
    const queriesUrl = `${theme.routes.predictive_search_url}?q=${query}&${typeParam}=query&${limitParam}=4&section_id=predictive-search-queries`;
    const countUrl = `${theme.routes.search_url}?q=${query}&type=product&options%5Bprefix%5D=last&section_id=search-results-count`;

    const fetchSection = (url) => fetch(url, { signal }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

    Promise.all([
      fetchSection(productsUrl),
      fetch(queriesUrl, { signal }).then((response) => response.ok ? response.text() : ''),
      fetch(countUrl, { signal }).then((response) => response.ok ? response.text() : '')
    ])
      .then(([productsText, queriesText, countText]) => {
        if (requestId !== this.searchRequestId || searchTerm !== this.getQuery()) return;
        const queriesMarkup = queriesText ? this.parseSectionHtml(queriesText, 'predictive-search-queries') : '';
        const productsMarkup = this.parseSectionHtml(productsText, 'predictive-search');
        const countNode = countText ? new DOMParser().parseFromString(countText, 'text/html').querySelector('[data-search-count]') : null;
        this.renderSearchResults(`${queriesMarkup}${productsMarkup}`, countNode ? countNode.dataset.searchCount : '');
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.searchRequestId) return;
        console.error('Predictive search request failed', error);
      })
      .finally(() => {
        if (requestId !== this.searchRequestId) return;
        this.results.classList.remove('loading');
      });
  }

  renderSearchResults(resultsMarkup, resultsCount) {
    if (!this.results) return;

    this.results.innerHTML = resultsMarkup;

    window.bassike?.rescanWishlistCollections();

    const submitButton = this.results.querySelector('#search-results-submit');
    if (!submitButton) return;

    const label = submitButton.querySelector('span');
    if (!label) return;

    const labelTemplate = submitButton.dataset.labelTemplate || label.textContent.trim();
    label.textContent = resultsCount ? `View ${resultsCount} Results` : labelTemplate;
  }
}

window.addEventListener('load', () => {
  new PredictiveSearch();
});
